import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogClose,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { OrganizationSelect } from "@/components/OrganizationSelect";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/components/ui/use-toast";
import { useDebounce } from '@/hooks/useDebounce';

export function CreateProjectDialog({ open, onOpenChange, onProjectCreated }) {
    const { toast } = useToast();
    const { axiosInstance } = useAuth();
    const [formData, setFormData] = useState({
        name: '',
        key: '',
        description: '',
        organizationId: ''
    });
    const [keyError, setKeyError] = useState('');
    const [isCheckingKey, setIsCheckingKey] = useState(false);
    const debouncedKey = useDebounce(formData.key, 500);
    const debouncedOrgId = useDebounce(formData.organizationId, 500);

    const generateKey = (name) => {
        return name
            .replace(/[^a-zA-Z]/g, '')
            .substring(0, 3)
            .toUpperCase();
    };

    useEffect(() => {
        const checkKeyExists = async () => {
            if (!debouncedKey || !debouncedOrgId) return;
            
            setIsCheckingKey(true);
            try {
                const response = await axiosInstance.get(`/projects/check-key/${debouncedKey}?organizationId=${debouncedOrgId}`);
                if (response.data.exists) {
                    setKeyError(`Project key "${debouncedKey}" already exists in this organization`);
                } else {
                    setKeyError('');
                }
            } catch (error) {
                console.error('Error checking project key:', error);
            } finally {
                setIsCheckingKey(false);
            }
        };

        checkKeyExists();
    }, [debouncedKey, debouncedOrgId]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (keyError) return;

        try {
            const response = await axiosInstance.post('/projects', formData);
            toast({
                description: "Project created successfully"
            });
            onOpenChange(false);
            if (onProjectCreated) onProjectCreated();
            setFormData({ name: '', key: '', description: '', organizationId: '' });
        } catch (error) {
            console.error('Error creating project:', error);
            toast({
                description: "Error creating project, please try again",
                variant: "destructive"
            });
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="text-2xl font-semibold">Add project details</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-muted-foreground">
                    Unlock the potential of AI-powered easy collaboration with your team. Edit project details anytime in settings.
                </p>
                <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                    <div className="space-y-2">
                        <Label htmlFor="name">Name</Label>
                        <Input
                            id="name"
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                name: e.target.value,
                                key: generateKey(e.target.value)
                            }))}
                            required
                        />
                        <p className="text-xs text-muted-foreground">
                            Try a team name, product name, project goal, milestone, etc
                        </p>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="key">Project Key</Label>
                        <div className="flex items-center space-x-2">
                            <Input
                                id="key"
                                value={formData.key}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    key: e.target.value.toUpperCase()
                                }))}
                                className={`uppercase ${keyError ? 'border-red-500' : ''}`}
                                maxLength={10}
                                required
                            />
                        </div>
                        {keyError ? (
                            <p className="text-xs text-red-500">{keyError}</p>
                        ) : (
                            <p className="text-xs text-muted-foreground">
                                Choose a prefix for task IDs (e.g., {formData.key || 'ABC'}-123)
                            </p>
                        )}
                    </div>
                    <div className="py-2">
                        <hr />
                    </div>
                    <div className="space-y-2">
                        <Label>Organization</Label>
                        <OrganizationSelect
                            preselect
                            value={formData.organizationId}
                            onChange={(value) => setFormData(prev => ({
                                ...prev,
                                organizationId: value
                            }))}
                        />
                        <p className="text-xs text-muted-foreground">
                            Everyone in the organization will be able to view this project
                        </p>
                    </div>
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="outline" type="button">Cancel</Button>
                        </DialogClose>
                        <Button type="submit">Create Project</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
} 